import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '@/lib/api';
import {
  AlertTriangle, BookOpen, ChevronRight, ClipboardList, FileText, Layers,
} from 'lucide-react';

/** Task inbox for staff — sits in the top ribbon next to the profile.
 *  Every pending review is its own tappable notification that lands on the
 *  exact row needing the decision. Anything older than two days turns red. */

const GROUP_ICON = {
  syllabus_intakes: BookOpen,
  question_papers: FileText,
  syllabus_library: Layers,
};
const GROUP_TONE = {
  syllabus_intakes: 'bg-indigo-50 text-indigo-600',
  question_papers: 'bg-amber-50 text-amber-600',
  syllabus_library: 'bg-emerald-50 text-emerald-600',
};

function age(days) {
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

export function AdminTasksButton() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const load = () => apiGet('/admin/tasks').then(setData).catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;
  const total = data.total || 0;
  const groups = data.groups || [];

  const go = (url) => { setOpen(false); navigate(url); };

  return (
    <div className="relative">
      <button type="button" data-testid="admin-tasks-btn" title="Tasks"
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors">
        <ClipboardList className={`w-4 h-4 ${data.overdue ? 'text-rose-600' : 'text-slate-500'}`} />
        {total > 0 && (
          <span data-testid="admin-tasks-count"
            className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-white text-[9px] font-heading flex items-center justify-center ${data.overdue ? 'bg-rose-500' : 'bg-blue-500'}`}>
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open && (
        <>
          <button type="button" aria-label="Close tasks" onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default" />
          <div data-testid="admin-tasks-panel"
            className="absolute right-0 mt-2 w-[23rem] max-h-[70vh] overflow-y-auto z-50 rounded-2xl border border-slate-200 bg-white shadow-xl">

            <div className="sticky top-0 bg-white/95 backdrop-blur px-4 pt-4 pb-2.5 border-b border-slate-100">
              <p className="font-heading text-sm font-bold text-slate-900">Tasks</p>
              <p className="text-[11px] font-body text-slate-500">
                {total ? `${total} waiting` : 'Nothing waiting — all clear'}
                {data.overdue ? ` · ${data.overdue} older than ${data.overdue_after_days} days` : ''}
              </p>
            </div>

            {!groups.length && (
              <p className="text-[12px] font-body text-slate-400 px-4 py-6 text-center" data-testid="admin-tasks-empty">
                No pending reviews or approvals right now.
              </p>
            )}

            {groups.map(g => {
              const Icon = GROUP_ICON[g.key] || ClipboardList;
              return (
                <div key={g.key} data-testid={`admin-task-group-${g.key}`}>
                  <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-1">
                    <p className="text-[10px] font-heading uppercase tracking-[0.08em] text-slate-400">{g.label}</p>
                    <button type="button" data-testid={`admin-task-group-all-${g.key}`}
                      onClick={() => go(g.url)}
                      className="text-[10px] font-heading text-blue-600 hover:underline flex-shrink-0">
                      See all {g.count}
                    </button>
                  </div>

                  <ul>
                    {g.items.map(i => (
                      <li key={i.id || i.title}>
                        <button type="button" data-testid={`admin-task-item-${i.id || i.title}`}
                          onClick={() => go(i.url || g.url)}
                          className="w-full text-left flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 active:bg-slate-100 transition-colors border-l-2 border-transparent hover:border-blue-400">
                          <span className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${GROUP_TONE[g.key] || 'bg-slate-100 text-slate-500'}`}>
                            <Icon className="w-3.5 h-3.5" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-heading text-[12.5px] text-slate-800 truncate">
                              {i.title || i.id}
                            </span>
                            {i.subtitle ? (
                              <span className="block text-[11px] font-body text-slate-400 truncate">{i.subtitle}</span>
                            ) : null}
                            <span className={`block text-[10px] font-heading mt-0.5 ${i.overdue ? 'text-rose-600' : 'text-slate-400'}`}>
                              {i.overdue && <AlertTriangle className="w-2.5 h-2.5 inline mr-1 -mt-0.5" />}
                              {age(i.age_days)}{i.overdue ? ' · needs a decision' : ''}
                            </span>
                          </span>
                          <ChevronRight className="w-3.5 h-3.5 text-slate-300 mt-2 flex-shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>

                  {g.count > g.items.length && (
                    <button type="button" onClick={() => go(g.url)}
                      data-testid={`admin-task-more-${g.key}`}
                      className="w-full text-left px-4 py-2 text-[11px] font-body text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors">
                      + {g.count - g.items.length} more waiting
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default AdminTasksButton;
